// File: PublicApp.jsx (Floating Widget Redesign)
import React, { useState, useEffect, useRef } from "react";
import "./PublicApp.css";

function PublicApp() {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const historyEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Initialize Session ID
  const [sessionId] = useState(() => {
    // 1. Check URL query parameter (Server/iframe passes this)
    const params = new URLSearchParams(window.location.search);
    const urlSessionId = params.get("session_id");
    if (urlSessionId) {
      sessionStorage.setItem("chatSessionId", urlSessionId);
      return urlSessionId;
    }

    // 2. Check Session Storage (Persist across refreshes)
    const storedSessionId = sessionStorage.getItem("chatSessionId");
    if (storedSessionId) {
      return storedSessionId;
    }

    // 3. Generate New (Local Dev / First Visit)
    const newSessionId = "session-" + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("chatSessionId", newSessionId);
    return newSessionId;
  });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [question]);

  const scrollToBottom = () => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [history, isTyping, isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const streamPublicQuestion = async () => {
    if (!question.trim()) return;

    const currentQuestion = question;
    setQuestion("");
    setIsTyping(true);

    // Reset height
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    const encodedQuestion = encodeURIComponent(currentQuestion);
    // Use relative URL or configurable endpoint
    // const url = `http://localhost:8000/public/ask/stream?question=${encodedQuestion}&session_id=${sessionId}`;
    const url = `https://happy-meadow-f91da3c9f1f046f09b85c5675deb93bc.azurewebsites.net/public/ask/stream?question=${encodedQuestion}&session_id=${sessionId}`;

    // Add user message immediately
    setHistory(prev => [
      ...prev,
      { role: "user", content: currentQuestion },
      { role: "assistant", content: "" } // Placeholder for streaming
    ]);

    try {
      const response = await fetch(url);
      if (!response.ok || !response.body) throw new Error("Streaming failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let streamedAnswer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        // Clean up any potential SSE prefixes if backend sends them
        const cleanChunk = chunk.replace(/^data:\s?/gm, "");
        streamedAnswer += cleanChunk;

        setHistory(prev => {
          const newHistory = [...prev];
          const lastMsg = newHistory[newHistory.length - 1];
          if (lastMsg.role === "assistant") {
            lastMsg.content = streamedAnswer;
          }
          return newHistory;
        });
      }

    } catch (err) {
      console.error("Stream failed:", err);
      setHistory(prev => {
        const newHistory = [...prev];
        const lastMsg = newHistory[newHistory.length - 1];
        if (lastMsg.role === "assistant") {
          lastMsg.content += "<p class='error'>⚠️ Connection lost. Please try again.</p>";
        }
        return newHistory;
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      streamPublicQuestion();
    }
  };

  const handleSuggestionClick = (text) => {
    setQuestion(text);
  };

  return (
    <div className={`PublicApp ${isOpen ? "open" : "closed"}`}>
      {/* Launcher Button */}
      {!isOpen && (
        <button className="launcher-btn" onClick={toggleChat}>
          <span className="launcher-icon">💬</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <header className="app-header">
            <div className="header-left">
              <h1>IST Metadata Agent</h1>
              <span className="status-badge">Public Preview</span>
            </div>
            <div className="header-right">
              <button className="icon-btn" onClick={toggleChat}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </header>

          <div className="chat-container" onClick={(e) => {
            // Event delegation for dynamically rendered HTML
            const card = e.target.closest('.recommendation-card');
            if (card) {
              const action = card.getAttribute('data-action');
              const text = card.innerText;

              if (action === 'save_note') {
                // Mock save note functionality
                alert("Note saved to your profile! (Mock)");
              } else if (action !== 'none') {
                // If it's a specific action or just text, we could put it in input
                // For now, let's just generic handle it
                handleSuggestionClick(text);
                // Or if it's a resource link logic, handle here
              }
            }
          }}>
            {history.length === 0 ? (
              <div className="welcome-screen">
                <div className="welcome-icon">👋</div>
                <h2>Ask ISTgenie – your wish for IST magic is my command.</h2>
                <p>Ask me anything about the Integrated Survey Tool, documentation, or release notes.</p>

                <div className="suggestion-chips">
                  <button className="chip" type="button" onClick={() => handleSuggestionClick("What is the IST?")}>
                    What is the IST?
                  </button>
                  <button className="chip" type="button" onClick={() => handleSuggestionClick("How do I export data?")}>
                    How do I export data?
                  </button>
                  <button className="chip" type="button" onClick={() => handleSuggestionClick("Latest release notes")}>
                    Latest release notes
                  </button>
                </div>
              </div>
            ) : (
              <>
                {history.map((msg, idx) => (
                  <div key={idx} className={`message-row ${msg.role}`}>
                    <div className={`avatar ${msg.role}`}>
                      {msg.role === "user" ? "👤" : "🤖"}
                    </div>
                    <div className="message-bubble">
                      {msg.role === "assistant" ? (
                        <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="message-row assistant">
                    <div className="avatar agent">🤖</div>
                    <div className="message-bubble">
                      <div className="typing-indicator">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={historyEndRef} />
              </>
            )}
          </div>

          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                ref={textareaRef}
                placeholder="Ask a question..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button
                className="send-btn"
                onClick={streamPublicQuestion}
                disabled={!question.trim() || isTyping}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
            <div className="footer-note">
              Latest Release Notes for IST
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicApp;
